import { useEffect, useState } from 'react'
import { Link, useParams, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { getRequestDetail, submitBwsToken, approveRequest, deleteRequest, confirmDeletion, getDemoConfig } from '@/services/api'
import { StatusBadge } from '@/components/StatusBadge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useToast } from '@/hooks/use-toast'

export default function RequestDetail() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { toast } = useToast()
  const queryClient = useQueryClient()
  const [bwsToken, setBwsToken] = useState('')
  const [notes, setNotes] = useState('')
  const [targetHost, setTargetHost] = useState<string>('')
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [demoMode, setDemoMode] = useState(false)
  // Overrides PI a l'approbation (B2.9) : '' = laisser le defaut serveur.
  const [piBaseUrl, setPiBaseUrl] = useState('')
  const [useLetsencrypt, setUseLetsencrypt] = useState('')
  const [dnsEnabled, setDnsEnabled] = useState('')
  // ACME_MODE : défaut 'production' (cert reconnu, requis pour un vrai client qui
  // parle à la BCEAO ; le défaut pipeline serait staging = cert non reconnu = footgun).
  const [acmeMode, setAcmeMode] = useState('production')

  useEffect(() => {
    getDemoConfig().then((cfg) => setDemoMode(!!cfg.enabled)).catch(() => {})
  }, [])

  // List of hosts the operator can pick. Keep in sync with backend
  // settings.host_public_ips. If a host is added, add it here.
  const availableHosts = ['htzn-01', 'htzn-02']

  // Preset Maquette : pointe la PI sur le simulateur + desactive LE/DNS publics.
  const applyMaquettePreset = () => {
    setPiBaseUrl('https://10.99.0.2:8085')
    setUseLetsencrypt('false')
    setDnsEnabled('false')
  }

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['request-detail', id],
    queryFn: () => getRequestDetail(id!),
    enabled: !!id,
    retry: 2,
    refetchInterval: (query) => {
      // Poll every 5s if status is deleting
      return query.state.data?.status === 'deleting' ? 5000 : false
    },
  })

  const tokenMutation = useMutation({
    mutationFn: () => submitBwsToken(id!, bwsToken),
    onSuccess: () => {
      toast({ title: 'Token soumis avec succès' })
      queryClient.invalidateQueries({ queryKey: ['request-detail', id] })
      setBwsToken('')
    },
    onError: () => {
      toast({ title: 'Échec de la soumission du token', variant: 'destructive' })
    },
  })

  const approveMutation = useMutation({
    mutationFn: () => approveRequest(id!, {
      notes,
      targetHost: targetHost || data?.target_host || availableHosts[0],
      piBaseUrl: piBaseUrl || undefined,
      useLetsencrypt: useLetsencrypt === '' ? undefined : useLetsencrypt === 'true',
      dnsEnabled: dnsEnabled === '' ? undefined : dnsEnabled === 'true',
      acmeMode: acmeMode || undefined,
    }),
    onSuccess: () => {
      toast({ title: 'Demande approuvée, provisionnement démarré' })
      queryClient.invalidateQueries({ queryKey: ['request-detail', id] })
    },
    onError: () => {
      toast({ title: 'Échec de l\'approbation', variant: 'destructive' })
    },
  })

  const deleteMutation = useMutation({
    mutationFn: () => deleteRequest(id!),
    onSuccess: (response) => {
      setShowDeleteConfirm(false)
      if (response?.status === 'deleting') {
        toast({ title: 'Pipeline de nettoyage lancé' })
        queryClient.invalidateQueries({ queryKey: ['request-detail', id] })
      } else {
        toast({ title: 'Demande supprimée' })
        navigate('/ops-portal')
      }
    },
    onError: () => {
      toast({ title: 'Échec de la suppression', variant: 'destructive' })
    },
  })

  const confirmDeletionMutation = useMutation({
    mutationFn: () => confirmDeletion(id!),
    onSuccess: () => {
      toast({ title: 'Client supprimé définitivement' })
      navigate('/ops-portal')
    },
    onError: () => {
      toast({ title: 'Échec de la confirmation', variant: 'destructive' })
    },
  })

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gold" />
      </div>
    )
  }

  if (isError || !data) {
    return (
      <div className="min-h-screen bg-background">
        <header className="bg-bronze shadow-md">
          <div className="container mx-auto px-4 py-4">
            <Link to="/ops-portal" className="text-white/70 hover:text-white text-sm font-medium">
              &larr; Retour au tableau de bord
            </Link>
          </div>
        </header>
        <main className="container mx-auto px-4 py-8 text-center">
          <p className="text-destructive mb-4">Impossible de charger les détails de la demande</p>
          <Button variant="outline" onClick={() => refetch()}>Réessayer</Button>
        </main>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="bg-bronze shadow-md">
        <div className="container mx-auto px-4 py-4">
          <Link to="/ops-portal" className="text-white/70 hover:text-white text-sm font-medium">
            &larr; Retour au tableau de bord
          </Link>
          <div className="flex items-center justify-between mt-2">
            <h1 className="text-xl font-bold text-white">{data.client_name}</h1>
            <StatusBadge status={data.status} />
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Request Details */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-lg shadow-sm border border-border p-6">
              <h2 className="text-lg font-semibold text-bronze mb-4 pb-2 border-b border-border">
                Détails de la Demande
              </h2>

              <dl className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <dt className="text-sm font-medium text-muted-foreground">Email de Contact</dt>
                  <dd className="mt-1 text-foreground">{data?.contact_email}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-muted-foreground">Date de Création</dt>
                  <dd className="mt-1 text-foreground">
                    {data?.created_at && new Date(data.created_at).toLocaleString('fr-FR')}
                  </dd>
                </div>
                {data?.target_host && (
                  <div>
                    <dt className="text-sm font-medium text-muted-foreground">Host Cible</dt>
                    <dd className="mt-1 font-mono text-foreground">{data.target_host}</dd>
                  </div>
                )}
                {data?.bws_project_id && (
                  <div>
                    <dt className="text-sm font-medium text-muted-foreground">BWS Project ID</dt>
                    <dd className="mt-1 font-mono text-sm text-foreground">{data.bws_project_id}</dd>
                  </div>
                )}
                {data?.pipeline_url && (
                  <div>
                    <dt className="text-sm font-medium text-muted-foreground">Pipeline</dt>
                    <dd className="mt-1">
                      <a
                        href={data.pipeline_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-gold hover:text-gold/80 font-medium"
                      >
                        Voir le Pipeline &rarr;
                      </a>
                    </dd>
                  </div>
                )}
                {data?.environment_url && (
                  <div className="md:col-span-2">
                    <dt className="text-sm font-medium text-muted-foreground">URL de l'Environnement</dt>
                    <dd className="mt-1">
                      <a
                        href={data.environment_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-gold hover:text-gold/80 font-medium"
                      >
                        {data.environment_url}
                      </a>
                    </dd>
                  </div>
                )}
                {data?.error_message && (
                  <div className="md:col-span-2">
                    <dt className="text-sm font-medium text-destructive">Message d'Erreur</dt>
                    <dd className="mt-1 text-destructive bg-destructive/10 p-3 rounded">
                      {data.error_message}
                    </dd>
                  </div>
                )}
              </dl>
            </div>

            {!demoMode && (data?.code_membre || data?.nom_participant || data?.pays) && (
              <div className="bg-white rounded-lg shadow-sm border border-border p-6 mt-6">
                <h2 className="text-lg font-semibold text-bronze mb-4 pb-2 border-b border-border">
                  Informations Participant
                </h2>
                <dl className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {data?.code_membre && (
                    <div>
                      <dt className="text-sm font-medium text-muted-foreground">Code Membre</dt>
                      <dd className="mt-1 font-mono text-foreground">{data.code_membre}</dd>
                    </div>
                  )}
                  {data?.pays && (
                    <div>
                      <dt className="text-sm font-medium text-muted-foreground">Pays</dt>
                      <dd className="mt-1 text-foreground">{data.pays}</dd>
                    </div>
                  )}
                  {data?.nom_participant && (
                    <div className="md:col-span-2">
                      <dt className="text-sm font-medium text-muted-foreground">Nom du Participant</dt>
                      <dd className="mt-1 text-foreground">{data.nom_participant}</dd>
                    </div>
                  )}
                  {data?.type_participant && (
                    <div>
                      <dt className="text-sm font-medium text-muted-foreground">Type</dt>
                      <dd className="mt-1 text-foreground">{data.type_participant}</dd>
                    </div>
                  )}
                  {data?.ville && (
                    <div>
                      <dt className="text-sm font-medium text-muted-foreground">Ville</dt>
                      <dd className="mt-1 text-foreground">{data.ville}</dd>
                    </div>
                  )}
                  {data?.quartier && (
                    <div>
                      <dt className="text-sm font-medium text-muted-foreground">Quartier</dt>
                      <dd className="mt-1 text-foreground">{data.quartier}</dd>
                    </div>
                  )}
                  {data?.participant_api_url && (
                    <div className="md:col-span-2">
                      <dt className="text-sm font-medium text-muted-foreground">URL API Participant</dt>
                      <dd className="mt-1 font-mono text-sm text-foreground break-all">{data.participant_api_url}</dd>
                    </div>
                  )}
                </dl>
              </div>
            )}
          </div>

          {/* Actions Panel */}
          <div className="lg:col-span-1">
            {data?.status === 'pending' && (
              <div className="bg-white rounded-lg shadow-sm border border-border p-6 space-y-6">
                <h2 className="text-lg font-semibold text-bronze pb-2 border-b border-border">
                  Actions
                </h2>

                {/* BWS Token Submission */}
                <div>
                  <h3 className="font-medium text-foreground mb-3 flex items-center gap-2">
                    <span className="w-6 h-6 rounded-full bg-gold text-white flex items-center justify-center text-sm">1</span>
                    Token BWS
                  </h3>
                  <div className="space-y-3">
                    <div>
                      <Label htmlFor="bws_token" className="sr-only">
                        Token BWS
                      </Label>
                      <Input
                        id="bws_token"
                        type="password"
                        placeholder="0.client_id.secret..."
                        value={bwsToken}
                        onChange={(e) => setBwsToken(e.target.value)}
                        disabled={data?.has_bws_token}
                        className="border-input focus:ring-gold focus:border-gold"
                      />
                    </div>
                    <Button
                      onClick={() => tokenMutation.mutate()}
                      disabled={!bwsToken || tokenMutation.isPending || data?.has_bws_token}
                      className={data?.has_bws_token ? 'bg-green-600 hover:bg-green-600' : 'bg-gold hover:bg-gold/90'}
                      size="sm"
                    >
                      {data?.has_bws_token ? 'Token Soumis' : 'Soumettre le Token'}
                    </Button>
                  </div>
                </div>

                {/* Approval */}
                <div>
                  <h3 className="font-medium text-foreground mb-3 flex items-center gap-2">
                    <span className={`w-6 h-6 rounded-full flex items-center justify-center text-sm ${data?.has_bws_token ? 'bg-gold text-white' : 'bg-muted text-muted-foreground'}`}>2</span>
                    Approbation
                  </h3>
                  <div className="space-y-3">
                    <div>
                      <Label htmlFor="target_host" className="text-sm text-muted-foreground">Host cible</Label>
                      <select
                        id="target_host"
                        value={targetHost || data?.target_host || availableHosts[0]}
                        onChange={(e) => setTargetHost(e.target.value)}
                        disabled={!data?.has_bws_token}
                        className="mt-1 w-full px-3 py-2 border border-input rounded-md text-sm bg-background focus:ring-gold focus:border-gold disabled:opacity-50"
                      >
                        {availableHosts.map((h) => (
                          <option key={h} value={h}>{h}</option>
                        ))}
                      </select>
                    </div>
                    {/* Overrides PI (avance / maquette) - B2.9 */}
                    <div className="border-t border-input pt-3 space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-muted-foreground">Cible PI (avancé)</span>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={applyMaquettePreset}
                          disabled={!data?.has_bws_token}
                        >
                          Preset Maquette
                        </Button>
                      </div>
                      <div>
                        <Label htmlFor="pi_base_url" className="text-sm text-muted-foreground">URL PI (vide = sandbox BCEAO)</Label>
                        <Input
                          id="pi_base_url"
                          value={piBaseUrl}
                          onChange={(e) => setPiBaseUrl(e.target.value)}
                          placeholder="https://sandbox.api.pi-bceao.com"
                          className="mt-1 border-input focus:ring-gold focus:border-gold"
                          disabled={!data?.has_bws_token}
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <Label htmlFor="use_letsencrypt" className="text-sm text-muted-foreground">Let's Encrypt</Label>
                          <select
                            id="use_letsencrypt"
                            value={useLetsencrypt}
                            onChange={(e) => setUseLetsencrypt(e.target.value)}
                            disabled={!data?.has_bws_token}
                            className="mt-1 w-full px-3 py-2 border border-input rounded-md text-sm bg-background focus:ring-gold focus:border-gold disabled:opacity-50"
                          >
                            <option value="">(défaut)</option>
                            <option value="true">true</option>
                            <option value="false">false</option>
                          </select>
                        </div>
                        <div>
                          <Label htmlFor="dns_enabled" className="text-sm text-muted-foreground">DNS Cloudflare</Label>
                          <select
                            id="dns_enabled"
                            value={dnsEnabled}
                            onChange={(e) => setDnsEnabled(e.target.value)}
                            disabled={!data?.has_bws_token}
                            className="mt-1 w-full px-3 py-2 border border-input rounded-md text-sm bg-background focus:ring-gold focus:border-gold disabled:opacity-50"
                          >
                            <option value="">(défaut)</option>
                            <option value="true">true</option>
                            <option value="false">false</option>
                          </select>
                        </div>
                      </div>
                      <div>
                        <Label htmlFor="acme_mode" className="text-sm text-muted-foreground">Let's Encrypt mode</Label>
                        <select
                          id="acme_mode"
                          value={acmeMode}
                          onChange={(e) => setAcmeMode(e.target.value)}
                          disabled={!data?.has_bws_token}
                          className="mt-1 w-full px-3 py-2 border border-input rounded-md text-sm bg-background focus:ring-gold focus:border-gold disabled:opacity-50"
                        >
                          <option value="production">production (vrai client BCEAO)</option>
                          <option value="staging">staging (tests, cert non reconnu)</option>
                        </select>
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="notes" className="text-sm text-muted-foreground">Notes (optionnel)</Label>
                      <Input
                        id="notes"
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        placeholder="Ajouter des notes..."
                        className="mt-1 border-input focus:ring-gold focus:border-gold"
                        disabled={!data?.has_bws_token}
                      />
                    </div>
                    <div className="flex flex-col gap-2">
                      <Button
                        onClick={() => approveMutation.mutate()}
                        disabled={!data?.has_bws_token || approveMutation.isPending}
                        className="bg-gold hover:bg-gold/90"
                      >
                        Approuver & Provisionner
                      </Button>
                      <Button
                        variant="destructive"
                        onClick={() => {
                          if (confirm('Êtes-vous sûr de vouloir supprimer cette demande ?')) {
                            deleteMutation.mutate()
                          }
                        }}
                        disabled={deleteMutation.isPending}
                      >
                        Rejeter & Supprimer
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {data?.status !== 'pending' && (
              <div className="bg-white rounded-lg shadow-sm border border-border p-6">
                <h2 className="text-lg font-semibold text-bronze mb-4 pb-2 border-b border-border">
                  Statut
                </h2>
                <div className="flex items-center gap-3 mb-4">
                  <StatusBadge status={data?.status || 'unknown'} />
                  <span className="text-muted-foreground text-sm">
                    {data?.status === 'approved' && 'En attente de provisionnement'}
                    {data?.status === 'running' && 'Provisionnement en cours'}
                    {data?.status === 'completed' && 'Environnement prêt'}
                    {data?.status === 'failed' && 'Le provisionnement a échoué'}
                    {data?.status === 'deleting' && 'Nettoyage en cours...'}
                    {data?.status === 'delete_failed' && 'Le nettoyage a échoué'}
                  </span>
                </div>

                {/* Deleting status - show pipeline link and confirm button */}
                {data?.status === 'deleting' && (
                  <div className="space-y-3 pt-3 border-t border-border">
                    {data?.pipeline_url && (
                      <a
                        href={data.pipeline_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-gold hover:text-gold/80 text-sm font-medium block"
                      >
                        Voir le pipeline de nettoyage &rarr;
                      </a>
                    )}
                    <p className="text-xs text-muted-foreground">
                      Une fois le pipeline terminé avec succès, cliquez ci-dessous pour supprimer l'entrée de la base de données.
                    </p>
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => confirmDeletionMutation.mutate()}
                      disabled={confirmDeletionMutation.isPending}
                    >
                      {confirmDeletionMutation.isPending ? 'Suppression...' : 'Confirmer la suppression'}
                    </Button>
                  </div>
                )}

                {/* Completed/Failed/Running - show delete button */}
                {(data?.status === 'completed' || data?.status === 'failed' || data?.status === 'delete_failed' || data?.status === 'running') && (
                  <div className="pt-3 border-t border-border">
                    {!showDeleteConfirm ? (
                      <Button
                        variant="outline"
                        className="text-destructive border-destructive hover:bg-destructive hover:text-white"
                        onClick={() => setShowDeleteConfirm(true)}
                      >
                        Supprimer ce client
                      </Button>
                    ) : (
                      <div className="space-y-3 p-3 bg-destructive/5 rounded-lg border border-destructive/20">
                        <p className="text-sm text-destructive font-medium">
                          Êtes-vous sûr de vouloir supprimer ce client ?
                        </p>
                        <p className="text-xs text-muted-foreground">
                          Cette action va déclencher un pipeline de nettoyage qui supprimera:
                          VMs, volumes, DNS, secrets Bitwarden, et l'état Terraform.
                        </p>
                        <div className="flex gap-2">
                          <Button
                            variant="destructive"
                            size="sm"
                            onClick={() => deleteMutation.mutate()}
                            disabled={deleteMutation.isPending}
                          >
                            {deleteMutation.isPending ? 'Suppression...' : 'Oui, supprimer'}
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setShowDeleteConfirm(false)}
                          >
                            Annuler
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  )
}
